// One project: its document live, the shared time range, the strip shown while the backend isn't
// live, and the product page for the route (#/p/<id>/<product>/<tab>).
import { createContext, lazy, Suspense, useContext, useEffect, useState, type ReactNode } from "react";
import { useConsole } from "../../App.tsx";
import { useDoc, useLoad, useStored, type Loaded, type Project } from "../../data/hooks.ts";
import { projectStats, type ProjectStats, type Range } from "../../data/stats.ts";
import { go, projectHref, type Route } from "../../router.ts";
import { productById } from "../../shell/nav.tsx";
import { toggleShortcut, useShortcuts } from "../../shell/shortcuts.ts";
import { Failed, Loading, PageHead, ProjectPill, RangePicker, Tabs } from "../../ui.tsx";
import { isFresh, isLive } from "./files.ts";
import { Overview } from "./Overview.tsx";
import { Activity } from "./Activity.tsx";
import { Story } from "./Story.tsx";

// Product pages load on first visit (the email renderer and rules compiler stay out of the first load).
const Database = lazy(() => import("./Database.tsx").then((m) => ({ default: m.Database })));
const Authentication = lazy(() => import("./Authentication.tsx").then((m) => ({ default: m.Authentication })));
const Storage = lazy(() => import("./Storage.tsx").then((m) => ({ default: m.Storage })));
const Realtime = lazy(() => import("./Realtime.tsx").then((m) => ({ default: m.Realtime })));
const Mail = lazy(() => import("./Mail.tsx").then((m) => ({ default: m.Mail })));
const Push = lazy(() => import("./Push.tsx").then((m) => ({ default: m.Push })));
const Usage = lazy(() => import("./Usage.tsx").then((m) => ({ default: m.Usage })));
const Settings = lazy(() => import("./Settings.tsx").then((m) => ({ default: m.Settings })));
const Migrate = lazy(() => import("./Migrate.tsx").then((m) => ({ default: m.Migrate })));

export interface ProjectCtx {
  id: string;
  p: Project;
  live: boolean;
  fresh: boolean;
  range: Range;
  setRange: (r: Range) => void;
  stats: Loaded<ProjectStats>;
}
const Ctx = createContext<ProjectCtx | null>(null);
export const useProject = () => useContext(Ctx)!;

/** The product page frame: head (kicker, title, pill, ☆, range), then Firebase-style tabs. */
export function ProductFrame({ product, tab, children, range = false, actions, title }: { product: string; tab?: string; children: ReactNode; range?: boolean; actions?: ReactNode; title?: string }) {
  const { id, p, range: r, setRange } = useProject();
  const def = productById(product)!;
  const page = tab ? `${product}/${tab}` : product;
  const pinned = useShortcuts(id).includes(page);
  return (
    <div className="page">
      <PageHead kicker={<>{p.name} <ProjectPill p={p} /></>} title={title ?? def.label} star={{ on: pinned, toggle: () => toggleShortcut(id, page) }}>
        {actions}
        {range && <RangePicker value={r} onChange={setRange} />}
      </PageHead>
      {def.tabs.length > 0 && <Tabs tabs={def.tabs} active={tab ?? def.tabs[0]!.id} href={(t) => projectHref(id, product, t)} />}
      {children}
    </div>
  );
}

function Strip({ p }: { p: Project }) {
  if (p.status === "deleting") return <div className="strip bad"><span className="spinner" />This project is being deleted, with its backend at {p.slug}.estra.cloud. If it stopped, finish it from <a href={projectHref(p.id, "settings", "general")}>Settings</a>.</div>;
  if (p.status === "queued" || p.status === "provisioning")
    return <div className="strip"><span className="spinner" />{p.apiUrl ? `Applying your changes to ${p.slug}.estra.cloud; it keeps running meanwhile.` : `Setting up ${p.slug}.estra.cloud. It usually takes a few minutes, and you'll get an email.`}{p.provisionStep ? ` ${p.provisionStep}…` : ""}</div>;
  if (p.status === "failed") return <div className="strip bad">{p.error ?? "Provisioning failed."} {p.apiUrl ? "Your backend keeps running with the previous files." : ""} <a href={projectHref(p.id, "database", "schema")}>Fix the files</a></div>;
  if (isFresh(p) && p.status === "draft") return <div className="strip bad">Your backend hasn't been requested yet. <a href={projectHref(p.id, "database", "schema")}>Create it</a></div>;
  if (!isFresh(p) && !p.apiUrl && p.status !== "ready") return <div className="strip">Upload your schema and rules to request your backend. <a href={projectHref(p.id, "migrate")}>Open the migration kit</a></div>;
  return null;
}

export function ProjectPage({ id, route }: { id: string; route: Route }) {
  const { user } = useConsole();
  const doc = useDoc(`projects/${id}`);
  const [range, setRange] = useStored<Range>("estra.console.range", "7d");
  const [wasDeleting, setWasDeleting] = useState(false);
  const p = doc.data;
  const stats = useLoad<ProjectStats>(p ? `stats:${id}:${range}` : null, () => projectStats(user.uid, id, range), 60_000);
  useEffect(() => {
    if (p?.status === "deleting") setWasDeleting(true);
    if (p === null && wasDeleting) go("#/");
  }, [p, wasDeleting]);
  if (doc.error) return <div className="page narrow"><Failed error={doc.error} /></div>;
  if (p === undefined) return <div className="page"><Loading /></div>;
  if (p === null) return <div className="page narrow"><h2>Project not found.</h2><p style={{ marginTop: 16 }}><a className="btn" href="#/">Back to projects</a></p></div>;
  const ctx: ProjectCtx = { id, p, live: isLive(p), fresh: isFresh(p), range, setRange, stats };
  const tab = route.tab;
  const pages: Record<string, ReactNode> = {
    overview: <Overview />,
    database: <Database tab={tab ?? "data"} />,
    auth: <Authentication tab={tab ?? "users"} />,
    storage: <Storage tab={tab ?? "files"} />,
    realtime: <Realtime tab={tab ?? "live"} />,
    mail: <Mail tab={tab ?? "overview"} />,
    push: <Push tab={tab ?? "overview"} />,
    usage: <Usage />,
    activity: <Activity />,
    story: <Story />,
    settings: <Settings tab={tab ?? "general"} />,
    migrate: <Migrate />,
  };
  return (
    <Ctx.Provider value={ctx}>
      <Strip p={p} />
      <Suspense fallback={<div className="page"><Loading /></div>}>
      {pages[route.product ?? "overview"] ?? <div className="page narrow"><h2>That page doesn't exist.</h2><p style={{ marginTop: 16 }}><a className="btn" href={projectHref(id)}>Project Overview</a></p></div>}
      </Suspense>
    </Ctx.Provider>
  );
}
